import * as React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  Box,
  Button,
  TextField,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  List,
} from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { useState, useEffect } from 'react';
import store from '../store/index';
import { getSmsTemplates } from '../utilities/api-utils';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { OrderSMSEvent, OrderSMSTemplate, allSmsTemplates } from '../models/orderSmsTemplate';
import Header from '../components/header';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}/>
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    alignItems: 'center'
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

export default function SmsTemplatesView() {
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [editSmsTemplates, setEditSmsTemplates] = useState<Array<OrderSMSTemplate>>([]);
  const [changedTemplates, setChangedTemplates] = useState<Array<string>>([]);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [cabinetId, setCabinetId] = useState<string>('');

  const loadSmsTemplates = async() => {
    const tempTemplates = [];

    try {
      const response = await getSmsTemplates(organizationId, cabinetId);
    
      if (response?.status !== 200) {
        alert(`Failed getting SMS templates for Org ID ${organizationId}, Cabinet Id ${cabinetId || 'not specified'}: ${response.data}`);
      } else {
        if (response.data.length !== 0) {
          for (const fetchedTemplate of response.data) {
            const template = new OrderSMSTemplate(fetchedTemplate);
            tempTemplates.push(template);
          }
  
          for (const template of allSmsTemplates()) {
            if (!tempTemplates.find(x => x.event_type === template.event_type)) {
              tempTemplates.push(template);
            }
          }
        }
      }
    } catch (e) {
      console.log('exception', e);
    }
    setEditSmsTemplates(tempTemplates);
  };

  useEffect(() => {
    setEditSmsTemplates([]);
    setChangedTemplates([]);
  
    const fetchTemplates = async () => {
      const response = await getSmsTemplates(store.getState().authorization.cloudKey, cabinetId);
      const tempTemplates = [];
    
      if (response?.status !== 200) {
        alert(`Failed getting SMS templates for Org ID ${organizationId}, Cabinet Id ${cabinetId || 'not specified'}: ${response.data}`);
      } else {
        if (response.data.length !== 0) {
          for (const fetchedTemplate of response.data) {
            const template = new OrderSMSTemplate(fetchedTemplate);
            tempTemplates.push(template);
          }
  
          for (const template of allSmsTemplates()) {
            if (!tempTemplates.find(x => x.event_type === template.event_type)) {
              tempTemplates.push(template);
            }
          }
  
          setEditSmsTemplates(tempTemplates);
        }
      }
    };

    if (organizationId) {
      fetchTemplates();
    }
  }, []);

  const upsertTemplates = async (template: OrderSMSTemplate) => {
    console.log('upsert template', template);
    const newList = changedTemplates.filter(x => x !== template.event_type);
    setChangedTemplates(newList);
  };

  const templateDisplayName = (templateEvent: string) => {
    switch (templateEvent) {
      case OrderSMSEvent.catch_all:
        return 'Catch All';
      case OrderSMSEvent.loaded:
        return 'Loaded';
      case OrderSMSEvent.pickup_expiring:
        return 'Pickup Expiring';
      case OrderSMSEvent.pickup_expired:
        return 'Pickup Expired';
      case OrderSMSEvent.transferred_to_counter:
        return 'Transferred to Counter';
    }
  };

  const onSelected = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const disableSave = (templateEvent: string) => {
    return !changedTemplates.find(x => x === templateEvent);
  };

  const editContent = (template: OrderSMSTemplate, content: string) => {
    setEditSmsTemplates((existingTemplates) =>
    existingTemplates.map((originalTemplate) => ({
        ...originalTemplate,
        content: originalTemplate.event_type === template.event_type ? content : originalTemplate.content
      }))
    );
    if (!changedTemplates.find(x => x === template.event_type)) {
      changedTemplates.push(template.event_type);
    }
  };

  const editAttachPickupQr = (template: OrderSMSTemplate, attach: boolean) => {
    setEditSmsTemplates((existingTemplates) =>
    existingTemplates.map((originalTemplate) => ({
        ...originalTemplate,
        attach_pickup_qr: originalTemplate.event_type === template.event_type ? attach : template.attach_pickup_qr
      }))
    );
    if (!changedTemplates.find(x => x === template.event_type)) {
      changedTemplates.push(template.event_type);
    }
  };

  const editActivated = (template: OrderSMSTemplate, activated: boolean) => {
    setEditSmsTemplates((existingTemplates) =>
    existingTemplates.map((originalTemplate) => ({
        ...originalTemplate,
        activated: originalTemplate.event_type === template.event_type ? activated : template.activated
      }))
    );
    if (!changedTemplates.find(x => x === template.event_type)) {
      changedTemplates.push(template.event_type);
    }
  };

  return (
    <div>
      <Header title='SMS Templates' />
      <Box 
        sx={{
          height: '500px',
          display: 'flex',
          flexDirection: 'column',
          marginTop: '25px'
        }}>
          <div
            style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center' }}>
              <TextField
                label="Org ID"
                style={{ marginRight: '20px', width: '250px'}}
                onChange={(e) => {
                  setOrganizationId(e.target.value);
                }} />
              <TextField
                label="Cabinet ID"
                onChange={(e) => {
                  setCabinetId(e.target.value);
                }}
                style={{ marginRight: '20px', width: '250px'}} />
              <Button 
                variant='contained'
                disabled={!organizationId}
                onClick={async() => { await loadSmsTemplates(); }}
                sx={{
                  marginBottom: '15px',
                  width: '70px'
                }}>
                Load
              </Button>
          </div>
          <List style={{maxHeight: '95%', overflow: 'auto'}}>
          {editSmsTemplates.map((template) => (
            <Accordion
              key={template.event_type}
              disableGutters={true}
              expanded={expanded === template.event_type}
              onChange={onSelected(template.event_type)} >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{templateDisplayName(template.event_type)}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  <Button 
                    variant='contained'
                    disabled={disableSave(template.event_type)}
                    onClick={async() => { await upsertTemplates(template); }}
                    sx={{
                      marginBottom: '15px',
                      width: '70px'
                    }}>
                    Save
                  </Button>
                  <TextField
                    label="SMS Content"
                    multiline
                    rows={4}
                    value={template.content}
                    onChange={(e) => {
                      editContent(template, e.target.value);
                    }}/>
                  <FormControlLabel control={
                    <Checkbox checked={template.activated} onChange={(e) => {
                      editActivated(template, e.target.checked);
                    }} />
                  } label='Activated' />
                  <FormControlLabel control={
                    <Checkbox checked={template.attach_pickup_qr} onChange={(e) => {
                      editAttachPickupQr(template, e.target.checked);
                    }} />
                  } label='Attach Pickup QR' />
                </FormGroup>
              </AccordionDetails>
            </Accordion>
            ))}
          </List>
      </Box>
    </div>
  );
}